import EventEmitter from 'events';
import nattyFetch from 'natty-fetch';
import { error } from '@ali/re-utils';
import functionParser from '@ali/vu-function-parser';
import parser from '../../store/parser';

function processParams(params) {
  const data = {};

  if (params && Array.isArray(params)) {
    params.forEach((item) => {
      const { name, value } = item;
      data[name] = value;
    });
  }

  return data;
}

class Remote {
  constructor(center, config, id, ...otherOptions) {
    this.type = 'REMOTE';
    this.center = center;
    this.otherOptions = otherOptions;
    this.config = config || {};
    this.url = this.config.url;
    this.params = [];
    if (Array.isArray(this.config.params)) {
      this.params = this.config.params;
    } else if (typeof this.config.params === 'string') {
      this.parseStringParams();
    }
    this.name = this.config.name;
    this.method = this.config.method || 'GET';
    this.exports = this.config.exports;
    this.shouldFetch = this.config.shouldFetch;
    this.isLoading = false;
    const context = otherOptions[0];
    if (context && context.fn) {
      this.fetch = context.fn.fetch;
    }

    this.willFetch = this.config.willFetch && functionParser(this.config.willFetch);
    this.didFetch = this.config.didFetch && functionParser(this.config.didFetch);
    this.fit = this.config.fit && functionParser(this.config.fit);
    this.onError = this.config.onError && functionParser(this.config.onError);
    this.variables = null;
    this.emitter = new EventEmitter();
    this.id = id;
  }

  getId() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  getVariables() {
    return this.variables;
  }

  getConfig(key) {
    return key ? this.config[key] : this.config;
  }

  load() {
    // bind this
    const context = this.otherOptions[0];
    if (context) {
      const actionContext = context.getActionContext();
      this.fit = this.fit && this.fit.bind(actionContext);
      this.willFetch = this.willFetch && this.willFetch.bind(actionContext);
      this.didFetch = this.didFetch && this.didFetch.bind(actionContext);
      this.onError = this.onError && this.onError.bind(actionContext);
    }
    const options = {
      url: this.queryUrl,
      jsonp: this.method === 'JSONP',
      method: this.method === 'JSONP' ? 'GET' : this.method,
      data: processParams(this.queryParams),
      willFetch: this.willFetch,
      withCredentials: true,
      Promise,
      fit: this.fit,
      onError: this.onError,
    };

    if (this.didFetch) {
      options.process = this.didFetch;
    }

    if (this.center.revo && this.center.revo.fetch) {
      options.fit = this.config.fit ? this.fit : undefined;
      return this.center.revo.fetch(options);
    }

    if (this.fetch) {
      return this.fetch(options);
    }
    return nattyFetch.create(options)();
  }

  makeQueryParams(params) {
    let queryParams = {};
    if (typeof this.config.params === 'string') {
      this.parseStringParams();
    }
    this.params.forEach((p) => {
      queryParams[p.name] = p.value;
    });
    queryParams = { ...queryParams, ...params };
    this.params = [];
    return Object.keys(queryParams).map((key) => {
      this.params.push({
        name: key,
        value: queryParams[key],
      });
      return {
        name: key,
        value: this.center.parse(queryParams[key], true),
      };
    });
  }

  makeQueryUrl() {
    return this.center.parse(this.url, true);
  }

  makeVariables() {
    if (!this.exports || !Array.isArray(this.exports) || this.exports.length < 1) {
      this.variables = this.data;
      return;
    }
    this.variables = {};
    if (this.data) {
      if (typeof this.data === 'object') {
        this.variables = this.data;
      }
      this.exports.forEach((item) => {
        this.variables[item.name] = parser(`\${${item.field}}`, this.data);
      });
    }
  }

  parseStringParams() {
    const queryJson = this.center.parse(this.config.params, true);
    if (Array.isArray(queryJson)) {
      this.params = queryJson;
    } else if (typeof queryJson === 'object') {
      this.params = Object.keys(queryJson).map(key => ({
        name: key,
        value: queryJson[key],
      }));
    }
  }

  execute(params = {}, options = {}) {
    const { loading, forceUpdate } = options;
    const context = (this.otherOptions || [])[0];

    if (this.shouldFetch && !this.center.parse(this.shouldFetch)) {
      // 本身没有变化
      return Promise.resolve({ value: this.getVariables(), changed: false });
    }

    // 1. prepare params
    const queryParams = this.makeQueryParams(params);

    // 2. prepare url
    const queryUrl = this.makeQueryUrl();

    if (!forceUpdate && !this.isLoading && this.queryUrl && this.queryParams
      && this.queryUrl === queryUrl
      && JSON.stringify(queryParams) === JSON.stringify(this.queryParams)) {
      let changed = false;
      if (this.valueSet) {
        this.makeVariables();
        changed = true;
      }
      // 本身没有变化
      return Promise.resolve({ value: this.getVariables(), changed });
    }

    this.queryUrl = queryUrl;
    this.queryParams = queryParams;

    if (this.variables && typeof this.variables === 'object') {
      this.variables.isLoading = true;
    } else {
      this.variables = {
        isLoading: true,
      };
    }

    // 3. load
    const executeTime = Date.now();
    this.executeTime = executeTime;
    this.isLoading = true;
    return new Promise((resolve, reject) => {
      let loadingToast = null;
      if (loading && context && context.fn && context.fn.toast) {
        loadingToast = context.fn.toast({
          type: 'loading',
          title: loading,
          duration: 10,
        });
      }
      this.load().then((data) => {
        if (loadingToast) loadingToast();
        this.isLoading = false;
        if (this.executeTime === executeTime) {
          this.data = data;
          this.valueSet = false;
          // 4. make exports
          this.makeVariables();
          // 重新叫过数据，标记自己已经变化过
          resolve({ value: this.getVariables(), changed: true });
        }
      }, (e) => {
        if (loadingToast) loadingToast();
        this.isLoading = false;
        const { method, url } = this;
        error(`数据源 '${this.name}' response error: ${e.message || e}.`, { method, params, url });
        if (this.executeTime === executeTime) {
          reject(e);
        }
      });
    });
  }

  onChange(func) {
    this.emitter.on('change', func);
    return () => {
      this.emitter.removeListener('change', func);
    };
  }

  setValue(value) {
    // 远程变量执行 setValue，认为它是变化的
    this.valueSet = true;
    this.data = value;
  }
}

export default Remote;
